import type { AssistantSegment } from "./assistantSegments.ts";
import { ACTIVITY_THINKING } from "./activityTexts.de.ts";

export const THINKING_STATUS_LABEL = ACTIVITY_THINKING;
export const THINKING_STATUS_MAX_CHARS = 80;

/**
 * Rapid tool steps would otherwise flash unreadably. Hold each status text
 * at least this long before swapping to the next one.
 */
export const STATUS_LINE_MIN_VISIBLE_MS = 400;

function lastSegment<T extends AssistantSegment>(
  segments: AssistantSegment[],
  predicate: (segment: AssistantSegment) => segment is T,
): T | undefined {
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];
    if (segment && predicate(segment)) return segment;
  }
  return undefined;
}

function runningToolLabel(segment: Extract<AssistantSegment, { type: "tool" }>): string {
  return segment.label.trim() || THINKING_STATUS_LABEL;
}

/** First non-empty reasoning line, collapsed, at most 80 characters. */
export function reasoningStatusText(text: string): string {
  const firstLine = text
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .find((line) => line.length > 0);
  if (!firstLine) return THINKING_STATUS_LABEL;
  if (firstLine.length <= THINKING_STATUS_MAX_CHARS) return firstLine;
  return `${firstLine.slice(0, THINKING_STATUS_MAX_CHARS - 1).trimEnd()}…`;
}

export function statusLineReplaceDelayMs(lastChangedAt: number, now: number): number {
  return Math.max(0, STATUS_LINE_MIN_VISIBLE_MS - (now - lastChangedAt));
}

/**
 * Live status text for an in-flight turn. Returns null only when the turn is
 * finished (`!isSending`). Mid-turn gaps (tool just finished, answer tokens
 * already started) keep `previousText` so the row is not unmounted.
 */
export function assistantStatusLine(
  segments: AssistantSegment[] | undefined,
  isSending: boolean,
  previousText: string | null = null,
): string | null {
  if (!isSending) return null;
  const list = segments ?? [];

  const running = lastSegment(
    list,
    (segment): segment is Extract<AssistantSegment, { type: "tool" }> =>
      segment.type === "tool" && segment.state === "running",
  );
  if (running) return runningToolLabel(running);

  const thinking = lastSegment(
    list,
    (segment): segment is Extract<AssistantSegment, { type: "reasoning" }> =>
      segment.type === "reasoning" && !segment.done,
  );
  if (thinking) return reasoningStatusText(thinking.text);

  const last = list[list.length - 1];
  if (last?.type === "tool") return runningToolLabel(last);

  return previousText?.trim() || THINKING_STATUS_LABEL;
}

export function assistantReasoningDisclosureText(segments: AssistantSegment[] | undefined): string {
  return (segments ?? [])
    .filter((segment): segment is Extract<AssistantSegment, { type: "reasoning" }> => segment.type === "reasoning")
    .map((segment) => segment.text.trim())
    .filter(Boolean)
    .join("\n\n");
}
